import { ProLayout, SettingDrawer } from '@ant-design/pro-components';
import {
  AccountBookOutlined,
  ApartmentOutlined,
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BellOutlined,
  BranchesOutlined,
  CalendarOutlined,
  CarOutlined,
  CloudDownloadOutlined,
  DatabaseOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  HomeOutlined,
  NotificationOutlined,
  ScheduleOutlined,
  SettingOutlined,
  SolutionOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { HeaderUserMenu } from '@/components/layout/HeaderUserMenu';
import { MessageBellBadge } from '@/components/layout/MessageBellBadge';
import { ReminderBellBadge } from '@/components/layout/ReminderBellBadge';
import { useMenuAccess, type MenuAccessRoute } from '@/hooks/use-menu-access';
import { useNotificationCounts } from '@/hooks/use-notification-counts';
import {
  ROUTE_APPLICATIONS,
  ROUTE_APPLICATIONS_DISPATCH_POOL,
  ROUTE_COST_RECORDS,
  ROUTE_DASHBOARDS,
  ROUTE_DEPARTMENTS,
  ROUTE_DICTIONARIES,
  ROUTE_DISPATCH_RECOMMEND_CONFIG,
  ROUTE_DISPATCHES,
  ROUTE_DRIVER_SCHEDULES,
  ROUTE_DRIVERS,
  ROUTE_FLOW_DEFINITIONS,
  ROUTE_FLOW_HOLIDAYS,
  ROUTE_FLOW_INSTANCES,
  ROUTE_FLOW_QUOTA,
  ROUTE_LOGIN_LOGS,
  ROUTE_MAINTENANCE_ORDERS,
  ROUTE_MAINTENANCE_RULES,
  ROUTE_MY_PERFORMANCE,
  ROUTE_MY_SCHEDULE,
  ROUTE_MY_TODO,
  ROUTE_NOTIFICATION_BLACKLIST,
  ROUTE_NOTIFICATION_CHANNELS,
  ROUTE_NOTIFICATION_EVENTS,
  ROUTE_NOTIFICATION_INBOX,
  ROUTE_NOTIFICATION_LOGS,
  ROUTE_NOTIFICATION_PREFERENCE,
  ROUTE_NOTIFICATION_TEMPLATES,
  ROUTE_OPERATION_LOGS,
  ROUTE_PERFORMANCE_INDICATORS,
  ROUTE_PERFORMANCE_RECORDS,
  ROUTE_QUOTA_POOLS,
  ROUTE_REMINDERS,
  ROUTE_REPORT_APPROVAL_EFFICIENCY,
  ROUTE_REPORT_CENTER,
  ROUTE_REPORT_COST,
  ROUTE_REPORT_DATASET,
  ROUTE_REPORT_DISPATCH,
  ROUTE_REPORT_DRIVER_WORKLOAD,
  ROUTE_REPORT_EXPORTS,
  ROUTE_REPORT_MAINTENANCE,
  ROUTE_REPORT_MONITOR,
  ROUTE_REPORT_NOTIFICATION_DELIVERY,
  ROUTE_REPORT_QUOTA_USAGE,
  ROUTE_REPORT_SUBSCRIPTIONS,
  ROUTE_REPORT_VEHICLE_UTILIZATION,
  ROUTE_REWARD_PENALTIES,
  ROUTE_SHIFT_TEMPLATES,
  ROUTE_USERS,
  ROUTE_VEHICLES,
  ROUTE_VEHICLE_GROUPS,
} from '@/constants/routes';
import { useThemeStore } from '@/stores/theme';
import appLogo from '@/assets/logo.png';

const REPORT_ACCESS = [
  'report.dispatch.view',
  'report.vehicle.view',
  'report.driver.view',
  'report.cost.view',
  'report.quota.view',
  'report.approval.view',
  'report.maintenance.view',
  'report.notification.view',
  'report.export',
  'report.subscription.self',
  'report.subscription.admin',
  'report.dataset.refresh',
  'report.dataset.backfill',
  'report.export.task.manage',
];

const ROUTES: MenuAccessRoute = {
  path: '/',
  routes: [
    {
      path: ROUTE_DASHBOARDS,
      name: '数据看板',
      icon: <BarChartOutlined />,
      access: 'dashboard.view',
    },
    {
      path: '/my-workbench-group',
      name: '我的工作台',
      icon: <HomeOutlined />,
      routes: [
        {
          path: ROUTE_MY_TODO,
          name: '我的待办',
          icon: <FileDoneOutlined />,
        },
        {
          path: ROUTE_NOTIFICATION_INBOX,
          name: '我的消息',
          icon: <BellOutlined />,
          access: 'notification.inbox.self',
        },
        {
          path: '/driver/dispatches',
          name: '我的派车（司机）',
          icon: <CarOutlined />,
          access: 'dispatch.driver_action',
        },
        {
          path: ROUTE_MY_SCHEDULE,
          name: '我的排班',
          icon: <CalendarOutlined />,
          access: 'driver.profile.read',
        },
        {
          path: ROUTE_MY_PERFORMANCE,
          name: '我的考核',
          icon: <FileSearchOutlined />,
          access: 'driver.profile.read',
        },
        {
          path: ROUTE_NOTIFICATION_PREFERENCE,
          name: '通知偏好',
          icon: <SettingOutlined />,
          access: 'notification.preference.self',
        },
      ],
    },
    {
      path: '/business-group',
      name: '用车业务',
      icon: <CarOutlined />,
      routes: [
        {
          path: ROUTE_APPLICATIONS,
          name: '用车申请',
          icon: <FileTextOutlined />,
          access: 'application.list',
        },
        {
          path: ROUTE_APPLICATIONS_DISPATCH_POOL,
          name: '调度池',
          icon: <FileDoneOutlined />,
          access: 'application.dispatch_pool',
        },
        {
          path: ROUTE_DISPATCHES,
          name: '派车单',
          icon: <ScheduleOutlined />,
          access: 'dispatch.list',
        },
        {
          path: ROUTE_DISPATCH_RECOMMEND_CONFIG,
          name: '推荐配置',
          icon: <ThunderboltOutlined />,
          access: 'dispatch.config',
        },
        {
          path: '/flow-config-group',
          name: '流程配置',
          icon: <BranchesOutlined />,
          routes: [
            {
              path: ROUTE_FLOW_DEFINITIONS,
              name: '流程定义',
              icon: <BranchesOutlined />,
              access: 'flow.definition.list',
            },
            {
              path: ROUTE_FLOW_INSTANCES,
              name: '流程实例',
              icon: <FileSearchOutlined />,
              access: 'flow.instance.list',
            },
            {
              path: ROUTE_FLOW_HOLIDAYS,
              name: '节假日管理',
              icon: <CalendarOutlined />,
              access: 'flow.holiday.list',
            },
            {
              path: ROUTE_FLOW_QUOTA,
              name: '紧急配额监控',
              icon: <BellOutlined />,
              access: 'flow.quota.view',
            },
          ],
        },
      ],
    },
    {
      path: '/report-group',
      name: '报表中心',
      icon: <BarChartOutlined />,
      access: REPORT_ACCESS,
      routes: [
        {
          path: ROUTE_REPORT_CENTER,
          name: '报表总览',
          icon: <BarChartOutlined />,
          access: REPORT_ACCESS,
        },
        {
          path: '/report-business-group',
          name: '业务报表',
          icon: <FileSearchOutlined />,
          routes: [
            {
              path: ROUTE_REPORT_DISPATCH,
              name: '派车统计',
              icon: <CarOutlined />,
              access: 'report.dispatch.view',
            },
            {
              path: ROUTE_REPORT_VEHICLE_UTILIZATION,
              name: '车辆利用率',
              icon: <AppstoreOutlined />,
              access: 'report.vehicle.view',
            },
            {
              path: ROUTE_REPORT_DRIVER_WORKLOAD,
              name: '司机工时',
              icon: <SolutionOutlined />,
              access: 'report.driver.view',
            },
            {
              path: ROUTE_REPORT_COST,
              name: '费用统计',
              icon: <AccountBookOutlined />,
              access: 'report.cost.view',
            },
            {
              path: ROUTE_REPORT_QUOTA_USAGE,
              name: '额度使用率',
              icon: <FileDoneOutlined />,
              access: 'report.quota.view',
            },
            {
              path: ROUTE_REPORT_APPROVAL_EFFICIENCY,
              name: '审批效率',
              icon: <FileTextOutlined />,
              access: 'report.approval.view',
            },
            {
              path: ROUTE_REPORT_MAINTENANCE,
              name: '维护成本',
              icon: <ToolOutlined />,
              access: 'report.maintenance.view',
            },
            {
              path: ROUTE_REPORT_NOTIFICATION_DELIVERY,
              name: '通知投递',
              icon: <NotificationOutlined />,
              access: 'report.notification.view',
            },
          ],
        },
        {
          path: '/report-manage-group',
          name: '报表管理',
          icon: <SettingOutlined />,
          routes: [
            {
              path: ROUTE_REPORT_EXPORTS,
              name: '导出任务',
              icon: <CloudDownloadOutlined />,
              access: ['report.export', 'report.export.task.manage'],
            },
            {
              path: ROUTE_REPORT_SUBSCRIPTIONS,
              name: '订阅管理',
              icon: <BellOutlined />,
              access: ['report.subscription.self', 'report.subscription.admin'],
            },
            {
              path: ROUTE_REPORT_MONITOR,
              name: '系统监控',
              icon: <AuditOutlined />,
              access: ['report.dataset.refresh', 'report.dataset.backfill', 'report.export.task.manage'],
            },
            {
              path: ROUTE_REPORT_DATASET,
              name: '数据管理',
              icon: <DatabaseOutlined />,
              access: ['report.dataset.refresh', 'report.dataset.backfill'],
            },
          ],
        },
      ],
    },
    {
      path: '/cost-quota-group',
      name: '费用与额度',
      icon: <AccountBookOutlined />,
      routes: [
        {
          path: ROUTE_QUOTA_POOLS,
          name: '额度池管理',
          icon: <FileDoneOutlined />,
          access: 'quota.pool.list',
        },
        {
          path: ROUTE_COST_RECORDS,
          name: '费用工单',
          icon: <FileTextOutlined />,
          access: 'cost.record.list',
        },
      ],
    },
    {
      path: '/basic-data-group',
      name: '基础数据',
      icon: <DatabaseOutlined />,
      routes: [
        {
          path: ROUTE_USERS,
          name: '用户管理',
          icon: <UserOutlined />,
          access: 'user.list',
        },
        {
          path: ROUTE_DEPARTMENTS,
          name: '部门管理',
          icon: <ApartmentOutlined />,
          access: 'department.list',
        },
        {
          path: ROUTE_VEHICLES,
          name: '车辆管理',
          icon: <CarOutlined />,
          access: 'vehicle.list',
        },
        {
          path: ROUTE_VEHICLE_GROUPS,
          name: '车辆分组',
          icon: <AppstoreOutlined />,
          access: 'vehicle.list',
        },
        {
          path: ROUTE_DRIVERS,
          name: '司机管理',
          icon: <SolutionOutlined />,
          access: 'driver.list',
        },
        {
          path: ROUTE_DICTIONARIES,
          name: '字典管理',
          icon: <DatabaseOutlined />,
          access: 'vehicle_type.manage',
        },
      ],
    },
    {
      path: '/schedule-performance-group',
      name: '排班与考核',
      icon: <CalendarOutlined />,
      routes: [
        {
          path: ROUTE_DRIVER_SCHEDULES,
          name: '排班日历',
          icon: <CalendarOutlined />,
          access: 'schedule.list',
        },
        {
          path: ROUTE_SHIFT_TEMPLATES,
          name: '班次模板',
          icon: <SettingOutlined />,
          access: 'schedule.template.manage',
        },
        {
          path: ROUTE_PERFORMANCE_INDICATORS,
          name: '考核指标',
          icon: <FileSearchOutlined />,
          access: 'performance.indicator.manage',
        },
        {
          path: ROUTE_PERFORMANCE_RECORDS,
          name: '考核记录',
          icon: <FileDoneOutlined />,
          access: 'performance.record.list',
        },
        {
          path: ROUTE_REWARD_PENALTIES,
          name: '奖惩管理',
          icon: <AccountBookOutlined />,
          access: 'performance.reward.manage',
        },
      ],
    },
    {
      path: '/maintenance-group',
      name: '车辆维护',
      icon: <ToolOutlined />,
      routes: [
        {
          path: ROUTE_MAINTENANCE_ORDERS,
          name: '维护工单',
          icon: <FileTextOutlined />,
          access: 'maintenance.list',
        },
        {
          path: ROUTE_REMINDERS,
          name: '到期提醒',
          icon: <BellOutlined />,
          access: 'reminder.list',
        },
        {
          path: ROUTE_MAINTENANCE_RULES,
          name: '维护规则',
          icon: <SettingOutlined />,
          access: 'maintenance.rule.manage',
        },
      ],
    },
    {
      path: '/notification-group',
      name: '通知中心',
      icon: <NotificationOutlined />,
      routes: [
        {
          path: ROUTE_NOTIFICATION_TEMPLATES,
          name: '消息模板',
          icon: <FileTextOutlined />,
          access: 'notification.template.list',
        },
        {
          path: ROUTE_NOTIFICATION_EVENTS,
          name: '事件接线',
          icon: <ApiOutlined />,
          access: 'notification.template.list',
        },
        {
          path: ROUTE_NOTIFICATION_LOGS,
          name: '发送日志',
          icon: <FileSearchOutlined />,
          access: 'notification.log.list',
        },
        {
          path: ROUTE_NOTIFICATION_CHANNELS,
          name: '通道配置',
          icon: <SettingOutlined />,
          access: 'notification.channel.config',
        },
        {
          path: ROUTE_NOTIFICATION_BLACKLIST,
          name: '黑名单管理',
          icon: <AuditOutlined />,
          access: 'notification.blacklist.manage',
        },
      ],
    },
    {
      path: '/log-group',
      name: '系统日志',
      icon: <AuditOutlined />,
      routes: [
        {
          path: ROUTE_OPERATION_LOGS,
          name: '操作日志',
          icon: <FileSearchOutlined />,
          access: 'operation_log.list',
        },
        {
          path: ROUTE_LOGIN_LOGS,
          name: '登录日志',
          icon: <FileTextOutlined />,
          access: 'login_log.list',
        },
      ],
    },
  ],
};

function findParentGroupPaths(
  routes: MenuAccessRoute[],
  pathname: string,
  parents: string[] = [],
): string[] {
  for (const route of routes) {
    const path = typeof route.path === 'string' ? route.path : undefined;

    if (path === pathname) return parents;

    if (route.routes && path) {
      const result = findParentGroupPaths(route.routes, pathname, [...parents, path]);
      if (result.length > 0) return result;
    }
  }

  return [];
}

export function BasicLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const settings = useThemeStore();
  const updateSettings = useThemeStore((s) => s.update);
  const menuRoutes = useMenuAccess(ROUTES);
  const { messageUnreadCount, reminderPendingCount } = useNotificationCounts();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const selectedKeys = [location.pathname];

  const [openKeys, setOpenKeys] = useState<string[]>(() =>
    findParentGroupPaths(menuRoutes.routes ?? [], location.pathname),
  );

  useEffect(() => {
    const parents = findParentGroupPaths(menuRoutes.routes ?? [], location.pathname);
    if (parents.length > 0) {
      setOpenKeys(parents);
    }
  }, [location.pathname, menuRoutes.routes]);

  const handleOpenChange = (keys: string[] | false) => {
    if (!keys) return;
    setOpenKeys(keys);
  };

  return (
    <>
      <ProLayout
        layout={settings.layout}
        navTheme={settings.navTheme}
        colorPrimary={settings.colorPrimary}
        contentWidth={settings.contentWidth}
        fixedHeader={settings.fixedHeader}
        fixSiderbar={settings.fixSiderbar}
        splitMenus={settings.splitMenus}
        title="派车系统"
        siderMenuType="sub"
        route={menuRoutes}
        location={{ pathname: location.pathname }}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        logo={appLogo}
        menu={{ locale: false }}
        onMenuHeaderClick={() => navigate(ROUTE_USERS)}
        menuItemRender={(item, defaultDom) =>
          item.path ? <Link to={item.path}>{defaultDom}</Link> : defaultDom
        }
        avatarProps={{
          render: () => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
              <MessageBellBadge count={messageUnreadCount} />
              <ReminderBellBadge count={reminderPendingCount} />
              <HeaderUserMenu onOpenSettings={() => setDrawerOpen(true)} />
            </span>
          ),
        }}
        breadcrumbRender={(routers = []) => [
          { path: '/', breadcrumbName: '首页' },
          ...routers,
        ]}
      >
        <Outlet />
      </ProLayout>

      <SettingDrawer
        enableDarkTheme
        themeOnly={false}
        getContainer={() => document.body}
        settings={settings}
        onSettingChange={(next) => {
          updateSettings({
            layout: next.layout as never,
            navTheme: next.navTheme as never,
            colorPrimary: next.colorPrimary ?? settings.colorPrimary,
            contentWidth: next.contentWidth as never,
            fixedHeader: next.fixedHeader ?? settings.fixedHeader,
            fixSiderbar: next.fixSiderbar ?? settings.fixSiderbar,
            splitMenus: next.splitMenus ?? settings.splitMenus,
          });
        }}
        collapse={drawerOpen}
        onCollapseChange={setDrawerOpen}
        disableUrlParams
      />
    </>
  );
}

